import type { ID } from '@octopus-do/octopus-project-core'
import type { CrawledPageBlocks } from '@store/crawlImport'

import { COLORS_HEX } from '@/constants/colors'
import { addProtocol, ucfirst } from '@/helpers'
import type {
  CrawlerExtra,
  ImportSitemapResultUrlItem,
  PageNameSource,
} from '@/types'

// A page whose parent holds at least this many children arrives collapsed, so a wide
// branch does not unfold across the whole canvas at once.
export const MIN_PAGES_TO_EXPAND = 15

// The two SEO blocks that carry a colour of their own. Kept as hex here and resolved
// against the destination project's palette by whoever lands the model.
const COLOR_SEO_TITLE = COLORS_HEX.purple
const COLOR_SEO_H1 = COLORS_HEX.topaz

const FALLBACK_SECTION_TITLE = 'Imported pages'

interface ImportModelSeo {
  seoDescription: string | null
  seoH1: string | null
  seoSlug: string | null
  seoTitle: string | null
}

export interface ImportModelNode extends ImportModelSeo {
  childIndex: number
  childrenNumber: number
  id: ID
  name: string
  // null means "a root of the crawled tree" — the caller decides what a root hangs off:
  // a section it just created, or the project's first section on the new-project path.
  parentId: ID | null
  url: string
}

export interface ImportModelBlock {
  childIndex: number
  // Hex rather than a colour id: the model knows nothing about a project's palette.
  colorHex: string | null
  id: ID
  name: string
  nodeId: ID
}

export interface ImportModel {
  blocks: ImportModelBlock[]
  // Pages the crawler laid out blocks for itself (OCT-4990). Disjoint from `blocks`:
  // a page the crawler matched gets its own blocks and no SEO text blocks, which would
  // only repeat the notes.
  crawledPages: CrawledPageBlocks[]
  nodes: ImportModelNode[]
  // Title the crawler read off the site. Only the new-project path uses it.
  siteTitle: string | null
}

export interface ImportPayload {
  data: Readonly<ImportSitemapResultUrlItem[]>
  extra: CrawlerExtra | undefined
  // The address the visitor asked to crawl. Absent on older crawler responses and on
  // the sitemap.xml path, so never rely on it alone.
  siteUrl?: string
  // Absent means URL names, as every crawl was named before OCT-5509. The public
  // generator sends it in the crawl it hands over through localStorage.
  pageNames?: PageNameSource
}

type PathItem = ImportSitemapResultUrlItem & { path: string | null }

interface PathTree {
  [segment: string]: PathTree
}

const decodeName = (raw: string): string => {
  const decoded = ((): string => {
    try {
      return decodeURI(raw)
    } catch (e) {
      console.error('Problem with decoding node name', e)
      return raw
    }
  })()

  return decoded.replace(/-/g, ' ').replace(/\.html$/, '')
}

const urlNameOf = (segment: string): string =>
  decodeName(segment === '/' ? 'Main page' : ucfirst(segment))

const pageTextOf = (
  meta: PathItem | undefined,
  source: PageNameSource | undefined
): string => {
  const text =
    (source === 'title' && meta?.title) || (source === 'h1' && meta?.h1) || ''

  return text.replace(/\s+/g, ' ').trim()
}

// The root stays "Main page" whatever the choice: it is the top of the URL tree,
// which a crawl started on a subfolder does not even reach as a page.
const nameOf = (
  segment: string,
  meta: PathItem | undefined,
  source: PageNameSource | undefined
): string => (segment !== '/' && pageTextOf(meta, source)) || urlNameOf(segment)

const hostOf = (url: string | null): string | null => {
  if (!url) {
    return null
  }

  try {
    return new URL(addProtocol(url)).hostname.replace(/^www\./, '') || null
  } catch {
    return null
  }
}

const slugOf = (url: string): string | null => {
  try {
    return new URL(url).pathname
  } catch {
    return null
  }
}

// A page only gets a slug when it also has a title — the two have always travelled
// together on this path, and splitting them would change what the new-project import writes.
const slugFor = (meta: PathItem | undefined): string | null => {
  if (!meta?.title || !meta.url) {
    return null
  }

  return slugOf(meta.url)
}

const toPathItems = (
  data: Readonly<ImportSitemapResultUrlItem[]>
): PathItem[] =>
  data.map((item) => {
    const url = addProtocol(item.url).replace(/\/$/, '').replace(/^\//, '')

    return { ...item, url, path: slugOf(url) }
  })

const toPathTree = (items: PathItem[]): PathTree => {
  const tree: PathTree = {}

  items.forEach((item) => {
    if (!item.path) {
      return
    }

    const parts = ['/', ...item.path.split('/').filter(Boolean)]

    parts.reduce((cursor, segment) => {
      cursor[segment] ||= {}
      return cursor[segment]
    }, tree)
  })

  return tree
}

const seoOf = (meta: PathItem | undefined): ImportModelSeo => ({
  seoDescription: meta?.description || null,
  seoH1: meta?.h1 || null,
  seoSlug: slugFor(meta),
  seoTitle: meta?.title || null,
})

/**
 * Names the section a crawl lands in. `siteUrl` is what the visitor typed, but the
 * sitemap.xml path does not send it and neither do older crawlers, so fall back to the
 * first page's host and finally to a fixed label — an empty section title reads as a bug.
 */
export const sectionTitleFromPayload = (payload: ImportPayload): string =>
  hostOf(payload.siteUrl ?? null) ??
  hostOf(payload.data[0]?.url ?? null) ??
  FALLBACK_SECTION_TITLE

/**
 * Turns a crawler response into a flat, redux-free description of what should be added:
 * pages in parent-before-child order, and the SEO blocks that hang off them.
 *
 * Deliberately knows nothing about the destination. Landing the model — in a fresh project
 * or in a section of an open one — is the caller's job, and the two differ in where roots
 * attach, which colours exist, and what "done" means.
 */
export const buildImportModel = (
  payload: ImportPayload,
  makeId: () => ID
): ImportModel => {
  const items = toPathItems(payload.data)
  const tree = toPathTree(items)

  const nodes: ImportModelNode[] = []
  const blocks: ImportModelBlock[] = []
  const crawledPages: CrawledPageBlocks[] = []

  const addBlock = (
    nodeId: ID,
    childIndex: number,
    name: string,
    colorHex: string | null
  ): void => {
    blocks.push({ childIndex, colorHex, id: makeId(), name, nodeId })
  }

  const walk = (
    branch: PathTree,
    path: string[],
    parentId: ID | null
  ): void => {
    Object.keys(branch).forEach((segment, index) => {
      const nodeId = makeId()
      const nextPath = path.concat(segment)
      const lookupPath = nextPath.join('/').slice(1) || '/'
      const meta = items.find((item) => item.path === lookupPath)
      const seo = seoOf(meta)

      // Pushed before recursing: consumers rely on a parent appearing ahead of its
      // children, because relations drop a child whose parent is not registered yet.
      nodes.push({
        ...seo,
        childIndex: index,
        childrenNumber: Object.keys(branch[segment]).length,
        id: nodeId,
        name: nameOf(segment, meta, payload.pageNames),
        parentId,
        url: meta?.url ?? '',
      })

      // An array — an empty one included — means the crawler matched the page and
      // says what blocks it has. Only a page it never reached falls back to the
      // title/description/h1 text blocks.
      if (meta?.blocks) {
        crawledPages.push({ nodeId, blocks: meta.blocks })
      } else {
        const named = [
          { color: COLOR_SEO_TITLE, name: seo.seoTitle },
          // Intentionally colourless: the description block has always taken the
          // project's default colour, unlike the title and H1 blocks.
          { color: null, name: seo.seoDescription },
          { color: COLOR_SEO_H1, name: seo.seoH1 },
        ].filter((block): block is { color: string | null; name: string } =>
          Boolean(block.name)
        )

        named.forEach((block, blockIndex) => {
          addBlock(nodeId, blockIndex, block.name, block.color)
        })
      }

      walk(branch[segment], nextPath, nodeId)
    })
  }

  walk(tree, [], null)

  return {
    blocks,
    crawledPages,
    nodes,
    siteTitle: payload.extra?.title || null,
  }
}
